/**
 * @file TabPanel + a11yProps - WCAG-compliant tab panel with accessibility helper.
 *
 * Implements the WAI-ARIA Tabs pattern (https://www.w3.org/WAI/ARIA/apg/patterns/tabs/).
 * Each TabPanel is linked to its controlling Tab via matching id/aria-labelledby attributes
 * generated by the exported `a11yProps()` helper.
 *
 * Inactive panels are both hidden (via the `hidden` attribute) and unmounted (conditional
 * rendering) to avoid unnecessary DOM weight and lifecycle effects in hidden tabs.
 *
 * @example
 *   // In the parent component:
 *   <Tabs value={tab} onChange={(e, v) => setTab(v)}>
 *     <Tab label="Ubersicht" {...a11yProps(0)} />
 *     <Tab label="Details"   {...a11yProps(1)} />
 *   </Tabs>
 *   <TabPanel value={tab} index={0}>Overview content</TabPanel>
 *   <TabPanel value={tab} index={1}>Detail content</TabPanel>
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
function TabPanel({ children, value, index, padding = 3, idPrefix = 'tabpanel', ...other }) {
  const isActive = value === index;

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`${idPrefix}-${index}`}
      aria-labelledby={`${idPrefix}-tab-${index}`}
      {...other}
    >
      {isActive && (
        <Box sx={{ p: padding }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  /** Panel content */
  children: PropTypes.node,
  /** Current active tab index */
  value: PropTypes.number.isRequired,
  /** This panel's index */
  index: PropTypes.number.isRequired,
  /** Box padding using theme spacing (default: 3) */
  padding: PropTypes.number,
  /** Prefix for accessibility IDs (default: 'tabpanel') */
  idPrefix: PropTypes.string,
};

TabPanel.defaultProps = {
  padding: 3,
  idPrefix: 'tabpanel',
};

/**
 * Generates the `id` and `aria-controls` attributes for an MUI Tab so it
 * correctly references its corresponding TabPanel. The `idPrefix` must match
 * the one passed to the paired TabPanel for the ARIA linkage to work.
 *
 * @param {number} index - Tab index (0-based)
 * @param {string} [idPrefix='tabpanel'] - Must match the TabPanel's idPrefix
 * @returns {{ id: string, 'aria-controls': string }}
 */
export function a11yProps(index, idPrefix = 'tabpanel') {
  return {
    id: `${idPrefix}-tab-${index}`,
    'aria-controls': `${idPrefix}-${index}`,
  };
}

export default TabPanel;
